import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Integrations.css';
import Navbar from '../../components/Navbar/Navbar';
import Aurora from '../../components/Aurora/Aurora';
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop';
import Footer from '../../components/Footer/Footer';

const Integrations = () => {
    const [navbarHidden, setNavbarHidden] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const viewportHeight = window.innerHeight;

            if (currentScrollY > lastScrollY && currentScrollY > viewportHeight) {
                setNavbarHidden(true);
            } else {
                setNavbarHidden(false);
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    const integrationMethods = [
        {
            id: 'saml',
            title: 'SAML 2.0 SSO',
            provider: 'Used by: Open eLMS',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            description: 'MyDigitalSchools acts as the Identity Provider (IdP). Users authenticate once and get seamless access to your platform.',
            partnerProvides: [
                'SP Metadata URL',
                'ACS (Assertion Consumer Service) URL',
                'Entity ID',
                'Attribute mapping requirements'
            ],
            mdsProvides: [
                'IdP Metadata XML',
                'Public Certificate',
                'SSO Service Endpoint',
                'User attributes (email, name, role)'
            ],
            hasDetailPage: true,
            detailLink: '/integrations/idp'
        },
        {
            id: 'oauth2',
            title: 'OAuth2 Client Credentials',
            provider: 'Used by: Boclips',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            description: 'Server-to-server authentication with per-user context. Ideal for API-based content providers.',
            partnerProvides: [
                'client_id and client_secret',
                'OAuth2 token endpoint',
                'API base URL',
                'User context header specification'
            ],
            mdsProvides: [
                'Token caching & refresh',
                'Per-user API calls',
                'Error handling & retries'
            ],
            hasDetailPage: false
        },
        {
            id: 'oidc',
            title: 'OAuth2 / OIDC (Authorization Code)',
            provider: 'Used by: Everybody Counts',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" strokeLinecap="round" />
                </svg>
            ),
            description: 'Authorization code flow with PKCE. MDS acts as Identity Provider; your app redirects users to us to sign in, then receives an authorization code and tokens.',
            partnerProvides: [
                'client_id and client_secret',
                'Callback URLs to allowlist',
                'PKCE code_verifier (recommended)'
            ],
            mdsProvides: [
                'Authorization, Token, UserInfo, JWKS endpoints',
                'ID token and access token (JWT, RS256)',
                'Claims: email, school_id, role, grade/section'
            ],
            hasDetailPage: true,
            detailLink: '/integrations/idp'
        },
        {
            id: 'token',
            title: 'Custom Token SSO',
            provider: 'Used by: Bookr Class',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            description: 'JWT-based token flow with callback validation. MDS issues tokens, your platform validates via our endpoint.',
            partnerProvides: [
                'SSO ID',
                'Client Secret',
                'Launch URL format',
                'Return URL handling'
            ],
            mdsProvides: [
                'JWT token generation',
                'Validation endpoint',
                'User payload (id, email, name, role)'
            ],
            hasDetailPage: false
        },
        {
            id: 'encrypted',
            title: 'Encrypted Payload SSO',
            provider: 'Used by: Highlights Library',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2 17l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2 12l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            description: 'AES-256-CBC encrypted payload with company-specific keys. Supports user registration, updates, and deletion.',
            partnerProvides: [
                'Company code',
                'Encryption key & IV',
                'SSO endpoint URL',
                'Field requirements & limits'
            ],
            mdsProvides: [
                'Encrypted payloads',
                'User data formatting',
                'Response decryption'
            ],
            hasDetailPage: false
        }
    ];

    return (
        <div className="integrations-page">
            <Navbar className={navbarHidden ? 'hidden' : ''} />

            {/* Aurora header */}
            <div className="integrations-aurora hero-section">
                <div className="aurora-wrapper">
                    <Aurora
                        colorStops={["#0088FF", "#4ecdc4", "#1E40AF"]}
                        blend={0.8}
                        amplitude={1.2}
                        speed={0.3}
                    />
                </div>
            </div>

            {/* Hero Section */}
            <section className="integrations-hero hero-section">
                <div className="integrations-hero-container">
                    <div className="integrations-hero-content">
                        <h1 className="integrations-hero-title">Integration Guide</h1>
                        <p className="integrations-hero-subtitle">
                            Everything you need to integrate your platform with MyDigitalSchools.
                            Choose the method that best fits your architecture.
                        </p>
                    </div>
                </div>
            </section>

            {/* Intro Section */}
            <section className="integrations-intro">
                <div className="integrations-container">
                    <div className="intro-content">
                        <h2 className="integration-section-title">How It Works</h2>
                        <p className="intro-text">
                            MyDigitalSchools supports multiple integration methods to accommodate different platform architectures.
                            Whether you use SAML, OAuth2, or custom token-based authentication, we can work with your system.
                            Review the options below to find the best fit for your integration needs.
                        </p>
                    </div>
                </div>
            </section>

            {/* Methods Section */}
            <section className="integrations-methods">
                <div className="integrations-container">
                    <h2 className="integration-section-title">Integration Methods</h2>
                    <div className="methods-grid">
                        {integrationMethods.map((method) => (
                            <div key={method.id} className="method-card">
                                <div className="method-icon">
                                    {method.icon}
                                </div>
                                <h3 className="method-title">{method.title}</h3>
                                <p className="method-provider">{method.provider}</p>
                                <p className="method-description">{method.description}</p>

                                <div className="method-details">
                                    <h4>Partner Provides</h4>
                                    <ul>
                                        {method.partnerProvides.map((item, idx) => (
                                            <li key={idx}>{item}</li>
                                        ))}
                                    </ul>

                                    <h4>MDS Provides</h4>
                                    <ul>
                                        {method.mdsProvides.map((item, idx) => (
                                            <li key={idx}>{item}</li>
                                        ))}
                                    </ul>
                                </div>

                                {method.hasDetailPage && (
                                    <Link to={method.detailLink} className="method-link">
                                        View Full Documentation
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="integrations-contact">
                <div className="integrations-container">
                    <div className="integration-contact-content">
                        <h2>Ready to Integrate?</h2>
                        <p>Reach out to our technical team to get started with your integration.</p>
                        <a href="mailto:integrations@mydigitalschools.com" className="contact-email">
                            integrations@mydigitalschools.com
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
            <ScrollToTop />
        </div>
    );
};

export default Integrations;
